import { useState, useEffect, useRef } from "react";

/**
 * Full-screen splash overlay with LumenForge branding.
 *
 * Phases:
 *   1. Fade IN  (fadeInDuration ms) — if 0, starts fully visible
 *   2. Hold     (holdTime ms)
 *   3. Fade OUT (fadeDuration ms) — then calls onComplete
 *
 * Uses a fixed CSS transition to avoid browser issues with changing
 * transition-duration and opacity in the same frame.
 */
export default function LoadingSplash({ onComplete, holdTime = 2000, fadeDuration = 500, fadeInDuration = 0, subtitle = "" }) {
  const [opacity, setOpacity] = useState(fadeInDuration > 0 ? 0 : 1);
  const [pointerEvents, setPointerEvents] = useState("all");
  // Use the longer duration so the CSS transition property never changes between renders
  const transitionDuration = Math.max(fadeInDuration || 0, fadeDuration, 1) / 1000;
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  useEffect(() => {
    let raf;
    const timers = [];
    const fadeIn = fadeInDuration || 0;

    if (fadeIn > 0) {
      // Phase 1: Fade in (opacity 0 → 1)
      raf = requestAnimationFrame(() => {
        requestAnimationFrame(() => setOpacity(1));
      });
      // Phase 2: Hold (already at opacity 1)
      // Phase 3: Fade out (opacity 1 → 0)
      timers.push(setTimeout(() => {
        setOpacity(0);
        setPointerEvents("none");
      }, fadeIn + holdTime));
      // Phase 4: Done
      timers.push(setTimeout(() => {
        if (onCompleteRef.current) onCompleteRef.current();
      }, fadeIn + holdTime + fadeDuration));
    } else {
      // No fade-in: start fully visible
      // Phase 3: Fade out
      timers.push(setTimeout(() => {
        setOpacity(0);
        setPointerEvents("none");
      }, holdTime));
      // Phase 4: Done
      timers.push(setTimeout(() => {
        if (onCompleteRef.current) onCompleteRef.current();
      }, holdTime + fadeDuration));
    }

    return () => {
      cancelAnimationFrame(raf);
      timers.forEach(clearTimeout);
    };
  }, [holdTime, fadeDuration, fadeInDuration]);

  return (
    <div
      data-testid="loading-splash"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 99999,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        background: "#000",
        opacity,
        transition: `opacity ${transitionDuration}s ease`,
        pointerEvents,
      }}
    >
      <img
        src="/lumenforge-f-logo.webp"
        alt="LumenForge"
        style={{ width: 72, height: 72 }}
      />
    </div>
  );
}
